import { orderBy } from 'lodash';
import { ProjectFiltersType } from '~/types/project/ProjectFiltersType';

interface TableState {
  column: string;
  data: any[];
  direction: 'asc' | 'desc';
  projectFilters: ProjectFiltersType;
}

interface TableAction {
  type: string;
  data?: any[];
  column?: string;
  projectFilters: ProjectFiltersType;
}

function matchesDeveloperName(project: any, filters: ProjectFiltersType): boolean {
  return !filters.developerName || project.installerName?.toLowerCase().includes(filters.developerName.toLowerCase());
}

function matchesInvestorName(project: any, filters: ProjectFiltersType): boolean {
  return !filters.investorName || project.investorName?.toLowerCase().includes(filters.investorName.toLowerCase());
}

// The "Bidding" filter covers all bidding-related statuses: BID_REVIEW is treated
// as BIDDING so the two are no longer surfaced (or filtered) as distinct statuses.
function normalizeStatusForFilter(statusDisplay: string): string {
  return statusDisplay === 'BID_REVIEW' ? 'BIDDING' : statusDisplay;
}

function matchesStatus(project: any, filters: ProjectFiltersType): boolean {
  return filters.selectedStatuses.length === 0 || filters.selectedStatuses.includes(normalizeStatusForFilter(project.statusDisplay));
}

function matchesOwner(project: any, filters: ProjectFiltersType): boolean {
  return filters.selectedOwners.length === 0 || filters.selectedOwners.includes(project.ownerId);
}

function matchesPricingType(project: any, filters: ProjectFiltersType): boolean {
  return !filters.selectedPricingType || project.pricingType === filters.selectedPricingType;
}

function matchesDeploymentType(project: any, filters: ProjectFiltersType): boolean {
  return !filters.selectedDeploymentType || project.deploymentType === filters.selectedDeploymentType;
}

function matchesDevelopmentStage(project: any, filters: ProjectFiltersType): boolean {
  return !filters.selectedDevelopmentStage || project.developmentStage === filters.selectedDevelopmentStage;
}

function hasOpenItems(project: any, filters: ProjectFiltersType): boolean {
  return !filters.hasOpenItems || project.hasOpenConversation || project.requiresCustomerProposal;
}

function includeArchived(project: any, filters: ProjectFiltersType): boolean {
  return filters.includeArchived || ((!project.isArchived && !project.portfolioIsArchived && project.status !== 'BID_REJECTED' && project.status !== 'DECLINED_TO_BID'));
}


export function filterProjects(projects: any[], filters: ProjectFiltersType) {
  return projects.filter(project =>
    matchesDeveloperName(project, filters) &&
    matchesInvestorName(project, filters) &&
    matchesStatus(project, filters) &&
    matchesOwner(project, filters) &&
    matchesPricingType(project, filters) &&
    matchesDeploymentType(project, filters) &&
    matchesDevelopmentStage(project, filters) &&
    hasOpenItems(project, filters) &&
    includeArchived(project, filters)
  );
}

export const DefaultSortOrders: { [key: string]: 'asc' | 'desc' } = {
  name: 'asc',
  developer: 'asc',
  projectCount: 'desc',
  pvMwdc: 'desc',
  bessMw: 'desc',
  bidType: 'asc',
  revenueType: 'asc',
  state: 'asc',
  status: 'asc',
  portfolioName: 'asc',
  investorName: 'asc',
  installerName: 'asc',
  statusDisplay: 'asc',
  location: 'asc',
  size: 'asc',
  bidDeadline: 'desc',
  updatedAt: 'asc'
};

export function projectListReducer(state: TableState, action: TableAction): TableState {
  const data = (action.data ?? state.data);

  const column = action.column ?? state.column;
  const sortData = (data: any) => {
    if (typeof data?.[column] !== 'string') {
      return data[column];
    } else if (column === 'location') {
      // Flip state and city so we can prioritize sort by state first
      const [state, city] = data[column]?.split(', ')?.reverse() ?? [];
      if (!state && !city) {
        return '';
      } else {
        return `${state}, ${city ?? ''}`.toLowerCase();
      }
    } else {
      return data?.[column]?.toLowerCase();
    }
  };

  const getSortedData = (data: any[], direction: 'asc' | 'desc') => {
    if (column === 'bidDeadline') {
      // No matter the direction, we want to prioritize projects with bid deadlines at the top.
      // Using a custom .sort instead of lodash orderBy to have more control over sorting
      return data.sort((a, b) => {
        if (!a.bidDeadline && !b.bidDeadline) {
          return 0;
        } else if (!a.bidDeadline) {
          return 1;
        } else if (!b.bidDeadline) {
          return -1;
        } else {
          const aTime = new Date(a.bidDeadline).getTime();
          const bTime = new Date(b.bidDeadline).getTime();
          return direction === 'asc' ? aTime - bTime : bTime - aTime;
        }
      });
    } else if (column === 'statusDisplay') {
      // Sort by statusDisplay first, and if the status is BIDDING sort further by bid deadline
      // For bidding statuses, we want to prioritize projects with bid deadlines at the top.
      // BID_REVIEW is normalized to BIDDING so the consolidated "Bidding" rows group together.
      return data.sort((a, b) => {
        const aStatus = normalizeStatusForFilter(a.statusDisplay);
        const bStatus = normalizeStatusForFilter(b.statusDisplay);
        if (aStatus === 'BIDDING' && bStatus === 'BIDDING') {
          if (!a.bidDeadline && !b.bidDeadline) {
            return 0;
          } else if (!a.bidDeadline) {
            return 1;
          } else if (!b.bidDeadline) {
            return -1;
          } else {
            const aTime = new Date(a.bidDeadline).getTime();
            const bTime = new Date(b.bidDeadline).getTime();
            return direction === 'asc' ? aTime - bTime : bTime - aTime;
          }
        }
        return direction === 'asc' ? aStatus.localeCompare(bStatus) : bStatus.localeCompare(aStatus);
      });
    }

    return orderBy(data, [sortData], [direction]);
  };
  const { projectFilters } = action;

  switch (action.type) {
    case 'INIT': {
      return {
        ...state,
        data: getSortedData(filterProjects(data ?? [], projectFilters), state.direction),
      };
    }

    case 'CHANGE_SORT': {
      // If the user clicks the same column, reverse the sort direction
      // Otherwise use the default sort order for the column
      const direction = action.column === state.column
        ? (state.direction === 'asc' ? 'desc' : 'asc')
        : (DefaultSortOrders[action.column as string] ?? 'asc');

      return {
        ...state,
        column,
        data: getSortedData(data, direction),
        direction,
      };
    }

    case 'CHANGE_FILTER': {
      // console.log('change filter', action.projectFilters);
      if (projectFilters) {
        const filteredData = filterProjects(data, projectFilters);

        // const ownerFilteredData = projectFilters.selectedOwner
        //   ? filteredData.filter(project => project.owner === projectFilters.selectedOwner)
        //   : filteredData;

        // const finalFilteredData = projectFilters.hasOpenItems
        //   ? ownerFilteredData.filter(project => project.hasOpenConversation || project.requiresCustomerProposal)
        //   : ownerFilteredData;

        return {
          ...state,
          projectFilters,
          data: getSortedData(filteredData, state.direction),
        };
      }

      return state;

    }
    default:
      throw new Error();
  }
}